import { useCallback, useEffect, useRef, useState } from 'react'

import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  type ConversationCartComponent,
  type ConversationCartItem,
} from '../../value-objects/ConversationTurnItem'
import {
  ConversationStreamStatuses,
  type ConversationStreamStatusEnum,
} from '../useConversationStream'

const QUANTITY_DEBOUNCE_MS = 1500
const MIN_QUANTITY = 1

export interface DecoratedCart {
  items: ConversationCartItem[]
  total: number
  currency: CurrencyName
  disabled: boolean
}

export interface UseCartResult {
  decorate: (cart: ConversationCartComponent) => DecoratedCart
  isContinuing: boolean
  increment: (itemId: string) => void
  decrement: (itemId: string) => void
  remove: (itemId: string) => void
  continueToOrderRequest: () => void
}

interface UseCartArgs {
  cart: Nullable<ConversationCartComponent>
  status: ConversationStreamStatusEnum
  sendSilentMessage: (message: string) => boolean
  onContinued: (reservedTab: Nullable<Window>) => void
}

const PLACEHOLDER_BODY_STYLE = [
  'display:flex',
  'align-items:center',
  'justify-content:center',
  'height:100vh',
  'margin:0',
  'font:400 16px/24px system-ui,-apple-system,sans-serif',
  'color:#5A6872',
].join(';')

// The reserved tab opens focused, so it carries a placeholder for the seconds
// the turn is still running rather than showing the user a blank page.
const renderPlaceholder = (tab: Window, message: string): void => {
  try {
    const { document } = tab
    document.title = message
    document.body.setAttribute('style', PLACEHOLDER_BODY_STYLE)
    const label = document.createElement('p')
    label.textContent = message
    document.body.appendChild(label)
  } catch {
    // an unwritable tab is still usable for the redirect
  }
}

// Browsers only allow opening a tab from inside a user gesture, and the
// checkout url is not known until the continue turn settles seconds later, so
// the tab is claimed on the click and pointed at the url afterwards. The opener
// link is cut immediately: the reserved tab navigates cross-origin.
const reserveTab = (placeholderMessage: string): Nullable<Window> => {
  const tab = window.open('', '_blank')
  if (!tab) return null
  tab.opener = null
  renderPlaceholder(tab, placeholderMessage)
  return tab
}

export const useCart = ({
  cart,
  status,
  sendSilentMessage,
  onContinued,
}: UseCartArgs): UseCartResult => {
  const intl = useProcurifyIntl()

  const [pendingQuantities, setPendingQuantities] = useState<
    Record<string, number>
  >({})
  const [pendingRemovalIds, setPendingRemovalIds] = useState<string[]>([])
  const [hasPendingTurn, setHasPendingTurn] = useState(false)
  const [isContinuing, setIsContinuing] = useState(false)

  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const cartKeyRef = useRef<Nullable<string>>(null)
  const pendingContinueRef = useRef(false)
  const reservedTabRef = useRef<Nullable<Window>>(null)

  const releaseReservedTab = useCallback((): Nullable<Window> => {
    const tab = reservedTabRef.current
    reservedTabRef.current = null
    return tab
  }, [])

  const resetOptimisticState = useCallback(() => {
    Object.values(timersRef.current).forEach(clearTimeout)
    timersRef.current = {}
    setPendingQuantities({})
    setPendingRemovalIds([])
    setHasPendingTurn(false)
  }, [])

  // The cart only changes by sending a silent turn and waiting for the agent to
  // stream back a fresh CART envelope. A new cart id or version reconciles the
  // predicted changes shown while the turn was in flight and re-enables the
  // controls — keying on both guards against a reused version on a new cart.
  useEffect(() => {
    if (!cart) return
    const cartKey = `${cart.cartId}:${cart.version}`
    if (cartKeyRef.current === cartKey) return
    cartKeyRef.current = cartKey
    resetOptimisticState()
  }, [cart, resetOptimisticState])

  useEffect(() => {
    if (status !== ConversationStreamStatuses.ERROR) return
    resetOptimisticState()
  }, [status, resetOptimisticState])

  // Redirect once the silent "continue" turn has settled. A failed turn clears
  // the pending flag so a later unrelated DONE never navigates away.
  useEffect(() => {
    if (!pendingContinueRef.current) return
    if (status === ConversationStreamStatuses.DONE) {
      pendingContinueRef.current = false
      setIsContinuing(false)
      onContinued(releaseReservedTab())
    } else if (status === ConversationStreamStatuses.ERROR) {
      pendingContinueRef.current = false
      setIsContinuing(false)
      releaseReservedTab()?.close()
    }
  }, [status, onContinued, releaseReservedTab])

  useEffect(
    () => () => {
      Object.values(timersRef.current).forEach(clearTimeout)
      reservedTabRef.current?.close()
    },
    []
  )

  const decorate = useCallback(
    (component: ConversationCartComponent): DecoratedCart => {
      // Only the latest cart envelope is interactive — older snapshots in the
      // transcript stay disabled so their controls never drive the live cart.
      const isLatest =
        cart !== null &&
        component.cartId === cart.cartId &&
        component.version === cart.version

      return {
        items: component.items
          .filter((item) => !(isLatest && pendingRemovalIds.includes(item.id)))
          .map((item) => ({
            ...item,
            quantity: isLatest
              ? (pendingQuantities[item.id] ?? item.quantity)
              : item.quantity,
          })),
        total: component.total,
        currency: component.currency,
        disabled:
          !isLatest ||
          hasPendingTurn ||
          status === ConversationStreamStatuses.STREAMING,
      }
    },
    [cart, pendingQuantities, pendingRemovalIds, hasPendingTurn, status]
  )

  const scheduleQuantityRequest = useCallback(
    (itemId: string, name: string, quantity: number) => {
      clearTimeout(timersRef.current[itemId])
      timersRef.current[itemId] = setTimeout(() => {
        // Skipped while a turn is already streaming — leave the controls
        // enabled so the change can be retried instead of locking the cart.
        setHasPendingTurn(
          sendSilentMessage(
            intl.formatMessage(messages.setQuantity, { name, quantity })
          )
        )
      }, QUANTITY_DEBOUNCE_MS)
    },
    [intl, sendSilentMessage]
  )

  const requestQuantityChange = useCallback(
    (itemId: string, delta: number) => {
      const item = (cart?.items ?? []).find(
        (candidate) => candidate.id === itemId
      )
      if (!item) return

      const currentQuantity = pendingQuantities[itemId] ?? item.quantity
      const nextQuantity = Math.max(MIN_QUANTITY, currentQuantity + delta)
      if (nextQuantity === currentQuantity) return

      setPendingQuantities((prev) => ({ ...prev, [itemId]: nextQuantity }))
      scheduleQuantityRequest(itemId, item.name, nextQuantity)
    },
    [cart, pendingQuantities, scheduleQuantityRequest]
  )

  const increment = useCallback(
    (itemId: string) => requestQuantityChange(itemId, 1),
    [requestQuantityChange]
  )

  const decrement = useCallback(
    (itemId: string) => requestQuantityChange(itemId, -1),
    [requestQuantityChange]
  )

  const remove = useCallback(
    (itemId: string) => {
      const item = (cart?.items ?? []).find(
        (candidate) => candidate.id === itemId
      )
      if (!item) return

      if (
        !sendSilentMessage(
          intl.formatMessage(messages.removeItem, { name: item.name })
        )
      )
        return

      clearTimeout(timersRef.current[itemId])
      setPendingRemovalIds((prev) => [...prev, itemId])
      setHasPendingTurn(true)
    },
    [cart, intl, sendSilentMessage]
  )

  // The button spins from the click itself: continuing is the final step of
  // the conversation, so the spinner holds until the DONE redirect or an
  // ERROR re-enables the cart.
  const continueToOrderRequest = useCallback(() => {
    setIsContinuing(true)
    if (!sendSilentMessage(intl.formatMessage(messages.continueToOrderRequest)))
      return
    // Only a cart bound to a vendor can end in a vendor-hosted checkout, and
    // only the click still carries the activation a new tab needs. Carts
    // without one never claim a tab, so they never flash an unused one.
    if (cart?.vendor && !reservedTabRef.current) {
      reservedTabRef.current = reserveTab(
        intl.formatMessage(messages.preparingCheckout)
      )
    }
    pendingContinueRef.current = true
    setHasPendingTurn(true)
  }, [cart, intl, sendSilentMessage])

  return {
    decorate,
    isContinuing,
    increment,
    decrement,
    remove,
    continueToOrderRequest,
  }
}
